import React, { useEffect, useMemo, useState } from 'react';
import { hrmsListApplications, hrmsMoveStage, PIPELINE_STAGES, STAGE_LABEL, hrmsScoreAll, hrmsRouteTop, hrmsScoreOne } from '../../lib/hrmsApi';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { CalendarPlus, FilePlus2, Sparkles, ArrowRightLeft, Loader2 } from 'lucide-react';
import InterviewScheduleModal from './InterviewScheduleModal';
import OfferCreateModal from './OfferCreateModal';

const scoreColor = (n) => n >= 85 ? 'bg-emerald-600' : n >= 70 ? 'bg-emerald-500' : n >= 50 ? 'bg-amber-500' : 'bg-red-500';

const PipelinePage = () => {
  const { toast } = useToast();
  const [apps, setApps] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [active, setActive] = useState(null); // application clicked
  const [scheduleFor, setScheduleFor] = useState(null);
  const [offerFor, setOfferFor] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [routing, setRouting] = useState(false);

  const load = async () => setApps(await hrmsListApplications().catch(() => []));
  useEffect(() => { load(); }, []);

  const byStage = useMemo(() => {
    const m = Object.fromEntries(PIPELINE_STAGES.map(s => [s, []]));
    apps.forEach(a => { if (m[a.stage]) m[a.stage].push(a); });
    return m;
  }, [apps]);

  const onDrop = async (stage) => {
    if (!dragging || dragging.stage === stage) return;
    try {
      await hrmsMoveStage(dragging.id, stage);
      toast({ title: `Moved to ${STAGE_LABEL[stage]}` });
      load();
    } catch { toast({ title: 'Move failed' }); }
    setDragging(null);
  };

  return (
    <div data-testid="pipeline-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold">Recruitment pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">Drag candidates between stages · click a card to schedule interview or create offer.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={async () => {
            setScoring(true);
            try {
              const res = await hrmsScoreAll();
              toast({ title: 'AI screening queued', description: `${res.queued} applications will be scored in the background` });
              setTimeout(load, 4000);
              setTimeout(load, 12000);
            } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || '' }); }
            finally { setScoring(false); }
          }} disabled={scoring} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="pipeline-score-all">
            {scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" /> Auto-score with AI</>}
          </Button>
          <Button onClick={async () => {
            const th = Number(window.prompt('Minimum AI score to route to Screening (0-100)?', '70'));
            if (!th && th !== 0) return;
            setRouting(true);
            try {
              const res = await hrmsRouteTop({ min_score: th, limit: 25 });
              toast({ title: `${res.moved} moved to Screening`, description: `Threshold: ${res.threshold}` });
              load();
            } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || '' }); }
            finally { setRouting(false); }
          }} disabled={routing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pipeline-route-top">
            {routing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRightLeft className="h-4 w-4 mr-1" /> Route top matches</>}
          </Button>
        </div>
      </div>
      <div className="mt-6 overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {PIPELINE_STAGES.map((s) => (
            <div key={s} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(s)} className="w-72 shrink-0 rounded-xl bg-slate-100 border border-slate-200">
              <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-700">{STAGE_LABEL[s]}</div>
                <div className="text-xs text-slate-500">{byStage[s].length}</div>
              </div>
              <div className="p-2 space-y-2 min-h-[200px]">
                {byStage[s].map((a) => (
                  <div key={a.id} draggable onDragStart={() => setDragging(a)} onClick={() => setActive(a)} className="rounded-lg bg-white border border-slate-200 p-3 shadow-sm cursor-pointer hover:border-orange-400" data-testid={`app-card-${a.candidate_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm text-slate-900 min-w-0 truncate">{a.full_name}</div>
                      {a.ai_score?.score !== undefined && (
                        <div className={`shrink-0 rounded-full text-white text-[10px] font-bold px-2 py-0.5 ${scoreColor(a.ai_score.score)}`} title={a.ai_score.summary || ''} data-testid={`app-score-${a.candidate_id}`}>{a.ai_score.score}</div>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{a.job_title}</div>
                    <div className="text-[11px] text-slate-400 mt-1">{a.candidate_id} · {a.email}</div>
                    {a.expected_ctc && <div className="text-[11px] text-orange-600 mt-1">Exp CTC: {a.expected_ctc}</div>}
                  </div>
                ))}
                {byStage[s].length === 0 && <div className="text-center text-xs text-slate-400 py-6">Drop candidates here</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Candidate detail modal */}
      <Dialog open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }}>
        <DialogContent className="max-w-lg">
          <DialogTitle>{active?.full_name}</DialogTitle>
          {active && (
            <div className="mt-1 space-y-3">
              <div className="text-xs text-slate-500">{active.candidate_id} · {active.job_title}</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><b>Email:</b> {active.email}</div>
                <div><b>Mobile:</b> {active.mobile}</div>
                <div><b>Total exp:</b> {active.total_experience || '—'}</div>
                <div><b>Current CTC:</b> {active.current_ctc || '—'}</div>
                <div><b>Expected CTC:</b> {active.expected_ctc || '—'}</div>
                <div><b>Notice:</b> {active.notice_period || '—'}</div>
                <div className="col-span-2"><b>Current stage:</b> <span className="capitalize">{STAGE_LABEL[active.stage]}</span></div>
              </div>
              {active.cover_letter && <div className="text-xs bg-slate-50 border rounded-lg p-3 max-h-32 overflow-y-auto"><b>Cover letter:</b><br />{active.cover_letter}</div>}

              {/* AI screen block */}
              <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-3">
                {active.ai_score?.score !== undefined ? (
                  <div>
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      <div className="text-xs uppercase tracking-widest text-purple-700 font-semibold">AI screening</div>
                      <div className={`ml-auto rounded-full text-white text-xs font-bold px-2 py-0.5 ${scoreColor(active.ai_score.score)}`}>{active.ai_score.score}/100 · {active.ai_score.recommendation}</div>
                    </div>
                    <div className="mt-2 text-sm text-slate-800 font-medium">{active.ai_score.summary}</div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="text-emerald-700 font-semibold">Strengths</div>
                        <ul className="mt-1 space-y-0.5 list-disc list-inside text-slate-700">{(active.ai_score.strengths || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                      </div>
                      <div>
                        <div className="text-red-700 font-semibold">Concerns</div>
                        <ul className="mt-1 space-y-0.5 list-disc list-inside text-slate-700">{(active.ai_score.concerns || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                      </div>
                    </div>
                    {active.ai_score.error && <div className="mt-2 text-xs text-red-600">Last error: {active.ai_score.error}</div>}
                  </div>
                ) : (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">No AI score yet.</span>
                    <Button variant="outline" onClick={async () => {
                      try {
                        const s = await hrmsScoreOne(active.id);
                        setActive({ ...active, ai_score: s });
                        load();
                        toast({ title: `Scored ${s.score}/100`, description: s.summary });
                      } catch (err) { toast({ title: 'Scoring failed', description: err?.response?.data?.detail || '' }); }
                    }} className="h-8 px-2 border-purple-300 text-purple-700" data-testid="app-score-now"><Sparkles className="h-3.5 w-3.5 mr-1" /> Score now</Button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={() => { setScheduleFor(active); setActive(null); }} className="bg-blue-600 hover:bg-blue-700" data-testid="pipeline-schedule-interview"><CalendarPlus className="h-4 w-4 mr-1" /> Schedule interview</Button>
                <Button onClick={() => { setOfferFor(active); setActive(null); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pipeline-create-offer"><FilePlus2 className="h-4 w-4 mr-1" /> Create offer</Button>
                <Button variant="outline" onClick={() => setActive(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {scheduleFor && (
        <InterviewScheduleModal application={scheduleFor} open={!!scheduleFor} onClose={() => setScheduleFor(null)} onCreated={load} />
      )}
      {offerFor && (
        <OfferCreateModal application={offerFor} open={!!offerFor} onClose={() => setOfferFor(null)} onCreated={load} />
      )}
    </div>
  );
};

export default PipelinePage;
